import React, { createContext, useCallback, useContext, useState } from 'react';
import { Snackbar, Alert } from '@mui/material';

/**
 * App-wide toast notifications (MUI Snackbar + Alert).
 *
 * Usage:
 *   const notify = useNotify();
 *   notify('Saved', 'success');
 *   notify('2 of 5 could not be deleted', 'warning');
 *   notify.error('Save failed');   // convenience helpers also available
 *
 * Severities: 'success' | 'info' | 'warning' | 'error' (default 'info').
 */
const NotificationContext = createContext(() => {});

export function useNotify() {
  return useContext(NotificationContext);
}

export function NotificationProvider({ children }) {
  const [toast, setToast] = useState(null); // { message, severity, key }

  const notify = useCallback((message, severity = 'info') => {
    if (!message) return;
    setToast({ message, severity, key: Date.now() });
  }, []);

  // Convenience helpers: notify.success(...), notify.error(...), etc.
  notify.success = (m) => notify(m, 'success');
  notify.info = (m) => notify(m, 'info');
  notify.warning = (m) => notify(m, 'warning');
  notify.error = (m) => notify(m, 'error');

  const handleClose = (_e, reason) => {
    if (reason === 'clickaway') return;
    setToast(null);
  };

  return (
    <NotificationContext.Provider value={notify}>
      {children}
      <Snackbar
        key={toast?.key}
        open={!!toast}
        autoHideDuration={toast?.severity === 'error' ? 8000 : 4000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert onClose={handleClose} severity={toast.severity} variant="filled" sx={{ width: '100%' }}>
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </NotificationContext.Provider>
  );
}

export default NotificationProvider;
